/** Sliding number-mode phone screenshots. Used as one-click prompts on Generate Media. */
export const SLIDING_NUMBER_MODE_PROMPTS = [
    {
        id: "dark",
        label: "Number mode — dark",
        prompt:
            "Mobile game UI screenshot of a 4x4 sliding number puzzle grid in dark mode. Modern clean app design, dark slate background. 15 rounded square tiles with bold white numbers. Movable tiles adjacent to the empty slot have a subtle soft cyan glow and directional arrows pointing to the open space. The empty slot is a recessed dark recessed hole with inner shadow. Sleek modern typography, top bar showing move counter and timer, bottom navigation bar with gift icon. High quality, 8k UI vector concept.",
    },
    {
        id: "light",
        label: "Number mode — light",
        prompt:
            "Mobile game UI screenshot of a 4x4 sliding number puzzle grid in light mode. Minimalist clean app interface, bright off-white and soft light-gray theme. Rounded white tiles elevated with soft tactile drop shadows and clear dark numbers. Movable tiles next to the empty space feature a subtle soft blue outline. The empty space is a smooth recessed cut-out slot. Modern typography, top bar with moves and timer, polished iOS/Android game design, 8k resolution.",
    },
];

/** Resource types supported by POST /v1/ad/generate (admin UI + API). */
export const DEFAULT_STORY_ANIMATION_STORY_ID = "04ffc929-339d-44db-90ca-cb7e7228c81c";

export const AD_GENERATE_RESOURCE_TYPES = [
    {
        value: "JIGSAW_APP_ICON",
        label: "Jigsaw Muse app icon (1024×1024)",
        description: "Bedrock 1:1 text-to-image — saved as a 1024×1024 PNG.",
        width: 1024,
        height: 1024,
        mediaKind: "image",
        promptRequired: true,
        supportsReferenceImage: true,
        promptPlaceholder:
            'A premium 3D mobile app icon for a game named "Jigsaw Muse". The icon is a rounded square with a smooth, dark twilight purple gradient background. Centered in the icon is a single, beautifully crafted golden jigsaw puzzle piece. Elegantly embossed and integrated into the top surface of the gold piece is a stylized, flowing capital letter "M" with elegant, muse-like curves. The puzzle piece has a clear 3D depth with subtle metallic bevels, catching warm light from the bottom right and cool light from the top left. Fine, magical gold dust and subtle glowing sparkles float delicately around the piece, giving it an inspiring, creative aura. Professional UI/UX app store icon design, high-end mobile game asset, clean rendering, 8k, raytracing --no text words typography',
    },
    {
        value: "SLIDING_APP_ICON",
        label: "Sliding app icon (1024×1024)",
        description: "Bedrock 1:1 text-to-image — saved as a 1024×1024 PNG.",
        width: 1024,
        height: 1024,
        mediaKind: "image",
        promptRequired: true,
        supportsReferenceImage: true,
        promptPlaceholder:
            "A minimalist, high-impact square app icon for the mobile game Sliding Puzzle Quest, contained within a polished, glowing neon-blue circular frame against a dark, deep-blue technical background. Tight macro close-up focused on a single brightly lit golden-brown Waffle Face sliding puzzle tile with smiling blue eyes and syrup texture. Tile significantly offset to the bottom-right of the icon space. Majority of the icon (top and left) is a dark empty deeply shadowed slot with subtle dark blue glowing edge indicating the path of movement. No other busy background tiles, no hands, no text, no timers. Strong neon-blue rim light separating the tile from the dark background. Clean rounded square canvas corners for a final app icon shape.",
    },
    {
        value: "WORD_WHEEL_APP_ICON",
        label: "Word Wheel Quest app icon (1024×1024)",
        description: "Bedrock 1:1 text-to-image — saved as a 1024×1024 PNG.",
        width: 1024,
        height: 1024,
        mediaKind: "image",
        promptRequired: true,
        supportsReferenceImage: true,
        promptPlaceholder:
            'A premium, high-quality 3D mobile game app icon for "Word Wheel Quest". Full-bleed square canvas with NO rounded outer border frame and NO dark grey bezel — background fills edge to edge. Deep rich indigo-to-violet gradient with a subtle faded ancient adventure map and compass rose. Centered: polished bronze and brass metal word wheel with five spokes. Circular nodes contain bold white 3D block letters C, R, O, S, S. Luminous neon-blue particle trail connects C → R → O → S → S. Magical soft lighting, light flares on the trail, glossy high-end game asset. High contrast, centered composition. --no outer border frame --no UI chrome',
    },
    {
        value: "SLIDING_FEATURE_GRAPHIC",
        label: "Sliding feature graphic (1024×500)",
        description: "Bedrock 16:9 text-to-image — saved as a 1024×500 PNG.",
        width: 1024,
        height: 500,
        mediaKind: "image",
        promptRequired: true,
        supportsReferenceImage: true,
        promptPlaceholder:
            "A widescreen 1024x500 landscape Google Play Feature Graphic for a mobile puzzle game. On the right: a high-tech glowing electronic sliding puzzle board tilted at an angle, scrambled colorful cartoon kitchen/breakfast tiles with a smiling waffle character, digital timer reading 00:34.12, holographic lines and floating loose puzzle pieces. On the left: dark moody slightly out-of-focus competitive puzzle tournament hall with clean open negative space for a large title. Vibrant polished 3D digital illustration, cinematic lighting, neon accents, sparkling particles. 16:9 aspect ratio.",
    },
    {
        value: "IPHONE_BACKGROUND",
        label: "iPhone background (1290×2796)",
        description:
            "Bedrock 9:16 text-to-image, or crop a standalone story image — saved as a 1290×2796 PNG (iPhone wallpaper). S3: ad/iphone/background/{id}.png",
        width: 1290,
        height: 2796,
        mediaKind: "image",
        promptRequired: true,
        supportsReferenceImage: true,
        promptPlaceholder:
            "Tall portrait iPhone wallpaper, 1290×2796 pixels. Soft mint-to-teal mist gradient, translucent fog layers, empty copy space, no text or objects.",
    },
    {
        value: "STORY_ANIMATION",
        label: "Animation (story video)",
        description:
            "Amazon Bedrock Luma Ray2 — story image scaled to 720×720, centered on 1280×720 black frame. Output saved under story/{storyId}/video/ in S3.",
        mediaKind: "video",
        storyIdRequired: true,
        defaultStoryId: DEFAULT_STORY_ANIMATION_STORY_ID,
        supportsReferenceImage: false,
        promptRequired: false,
        promptPlaceholder:
            "Optional motion prompt, e.g. gentle cinematic zoom and ambient movement on the scene.",
    },
];

/** 1024×1024 app icons that support the multi-size ZIP download pack. */
export const APP_ICON_RESOURCE_TYPES = new Set([
    "JIGSAW_APP_ICON",
    "SLIDING_APP_ICON",
    "WORD_WHEEL_APP_ICON",
]);

/** @param {string} resourceType */
export function getAdGenerateResourceType(resourceType) {
    return AD_GENERATE_RESOURCE_TYPES.find((entry) => entry.value === resourceType) ?? null;
}

/** @param {{ imageUrl?: string, image_url?: string } | null | undefined} row */
export function getAdResourcePreviewUrl(row) {
    if (!row) return null;
    const imageUrl = row.imageUrl ?? row.image_url;
    if (typeof imageUrl === "string" && imageUrl.trim()) return imageUrl.trim();
    return null;
}

/** @param {{ videoUrl?: string, video_url?: string } | null | undefined} row */
export function getAdResourceVideoUrl(row) {
    if (!row) return null;
    const videoUrl = row.videoUrl ?? row.video_url;
    if (typeof videoUrl === "string" && videoUrl.trim()) return videoUrl.trim();
    return null;
}

/** Download / open URL — MP4 when {@link getAdResourceVideoUrl} is set, else preview image. */
export function getAdResourceMediaUrl(row) {
    return getAdResourceVideoUrl(row) ?? getAdResourcePreviewUrl(row);
}

/** @param {{ resourceType?: string, contentType?: string, content_type?: string, videoUrl?: string, video_url?: string } | null | undefined} row */
export function isVideoAdResource(row) {
    if (!row) return false;
    const typeConfig = getAdGenerateResourceType(row.resourceType);
    if (typeConfig?.mediaKind === "video") return true;
    const contentType = String(row.contentType ?? row.content_type ?? "").toLowerCase();
    if (contentType.startsWith("video/")) return true;
    return Boolean(getAdResourceVideoUrl(row));
}

/** @param {string | undefined | null} resourceType */
export function isAppIconResourceType(resourceType) {
    return APP_ICON_RESOURCE_TYPES.has(resourceType);
}

/** @param {{ resourceType?: string, width?: number, height?: number } | null | undefined} row */
export function isSquareResourcePreview(row) {
    return (
        isAppIconResourceType(row?.resourceType) ||
        (row?.width && row?.height && row.width === row.height)
    );
}
